/**
 * Integrytas Group - Base App (MOBILE)
 * Page: APR.SLA_AUTHORITY — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/ApprovalSlaAuthorityPage.tsx. That
 * desktop page maintains two APR engine reference tables in side-by-side grids:
 *   apr_sla_policy      — SLA due window + breach action per config/level.
 *   apr_authority_limit — approval authority thresholds per user/role.
 * This screen presents the SAME data/logic layer with a DISTINCT mobile UI:
 *   - a section switch (SLA policies | Authority limits) + KPI summary,
 *   - a dense card list with search + status facet chips per section,
 *   - a bottom-sheet detail, and an edit sheet that saves through the Common
 *     Routine via routedUpdate — §2 single write path; never a direct apiDb write.
 * Reads via apiDb; the config FK is resolved to its code (apr_config has no _tl —
 * its internal_code IS the display, per the desktop). It never re-renders the
 * desktop grid; §16. Add/delete of policies stays on the desktop, like the other
 * mobile screens keep structural edits there.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, SquarePen, Power, Timer, Gauge } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
} from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'APR.SLA_AUTHORITY';

// ---------------------------------------------------------------------------
// Row shapes (mirror the desktop selects exactly — no invented columns).
// ---------------------------------------------------------------------------

interface ConfigRow { approval_config_id: number; internal_code: string }

interface SlaRow {
  apr_sla_policy_id: number;
  approval_config_id: number;
  level: number | null;
  due_minutes: number;
  reminder_minutes: number | null;
  breach_action: string;
  use_business_calendar: 'Y' | 'N';
  business_calendar_code: string | null;
  is_active: 'Y' | 'N';
}

interface AuthRow {
  apr_authority_limit_id: number;
  scope: string;
  scope_ref: string;
  entity_name: string | null;
  dimension: string;
  max_value: number;
  is_active: 'Y' | 'N';
}

type Section = 'sla' | 'auth';
type StatusFacet = 'all' | 'active' | 'inactive';

// CHECK-enum labelled constants (§17.B11 — stored value stays the code, only the
// label is localized; never a bare literal in the JSX).
interface EnumOption { value: string; labelKey: string; fallback: string }

const BREACH_ACTIONS: EnumOption[] = [
  { value: 'NOTIFY', labelKey: 'mobile.apr_sla.breach.NOTIFY', fallback: 'Notify' },
  { value: 'ESCALATE', labelKey: 'mobile.apr_sla.breach.ESCALATE', fallback: 'Escalate' },
  { value: 'AUTO_APPROVE', labelKey: 'mobile.apr_sla.breach.AUTO_APPROVE', fallback: 'Auto-approve' },
  { value: 'AUTO_REJECT', labelKey: 'mobile.apr_sla.breach.AUTO_REJECT', fallback: 'Auto-reject' },
];

const SCOPES: EnumOption[] = [
  { value: 'USER', labelKey: 'mobile.apr_auth.scope.USER', fallback: 'User' },
  { value: 'ROLE', labelKey: 'mobile.apr_auth.scope.ROLE', fallback: 'Role' },
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function MobileApprovalSlaAuthorityPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const [configs, setConfigs] = useState<ConfigRow[]>([]);
  const [sla, setSla] = useState<SlaRow[]>([]);
  const [auth, setAuth] = useState<AuthRow[]>([]);
  const [section, setSection] = useState<Section>('sla');
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selectedSla, setSelectedSla] = useState<SlaRow | null>(null);
  const [selectedAuth, setSelectedAuth] = useState<AuthRow | null>(null);
  const [editingSla, setEditingSla] = useState<SlaRow | null>(null);
  const [editingAuth, setEditingAuth] = useState<AuthRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [c, s, a] = await Promise.all([
        apiDb.from('apr_config').select('approval_config_id, internal_code').order('internal_code'),
        apiDb
          .from('apr_sla_policy')
          .select('apr_sla_policy_id, approval_config_id, level, due_minutes, reminder_minutes, breach_action, use_business_calendar, business_calendar_code, is_active')
          .order('apr_sla_policy_id'),
        apiDb
          .from('apr_authority_limit')
          .select('apr_authority_limit_id, scope, scope_ref, entity_name, dimension, max_value, is_active')
          .order('apr_authority_limit_id'),
      ]);
      if (c.error) throw c.error;
      if (s.error) throw s.error;
      if (a.error) throw a.error;
      setConfigs((c.data ?? []) as ConfigRow[]);
      setSla((s.data ?? []) as SlaRow[]);
      setAuth((a.data ?? []) as AuthRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  // FK → display: apr_config has no _tl, so its internal_code is the display value.
  const configName = (id: number): string => {
    const hit = configs.find((c) => c.approval_config_id === id);
    /* fk-display-ok: apr_config has no _tl table; internal_code is its natural display, per the desktop page. */
    return hit?.internal_code ?? t('mobile.apr.unknown_config', 'Unknown config');
  };

  const enumLabel = (options: EnumOption[], value: string): string => {
    const hit = options.find((o) => o.value === value);
    return hit ? t(hit.labelKey, hit.fallback) : value;
  };

  const matchesStatus = (isActive: 'Y' | 'N'): boolean => {
    if (statusFacet === 'active') return isActive === 'Y';
    if (statusFacet === 'inactive') return isActive === 'N';
    return true;
  };

  const filteredSla = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return sla.filter((r) => {
      if (!matchesStatus(r.is_active)) return false;
      if (!q) return true;
      return (
        configName(r.approval_config_id).toLowerCase().includes(q) ||
        enumLabel(BREACH_ACTIONS, r.breach_action).toLowerCase().includes(q) ||
        (r.business_calendar_code ?? '').toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sla, configs, filter, statusFacet]);

  const filteredAuth = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return auth.filter((r) => {
      if (!matchesStatus(r.is_active)) return false;
      if (!q) return true;
      return (
        enumLabel(SCOPES, r.scope).toLowerCase().includes(q) ||
        r.scope_ref.toLowerCase().includes(q) ||
        (r.entity_name ?? '').toLowerCase().includes(q) ||
        r.dimension.toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, filter, statusFacet]);

  const sections: { key: Section; label: string }[] = [
    { key: 'sla', label: t('mobile.apr.section_sla', 'SLA policies') },
    { key: 'auth', label: t('mobile.apr.section_auth', 'Authority limits') },
  ];

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.apr.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const minutesLabel = (n: number | null): string =>
    n === null ? t('common.none', 'None') : t('mobile.apr.minutes', '{{n}} min', { n });

  const activeSlaCount = useMemo(() => sla.filter((r) => r.is_active === 'Y').length, [sla]);
  const activeAuthCount = useMemo(() => auth.filter((r) => r.is_active === 'Y').length, [auth]);

  const toggleSlaActive = async (r: SlaRow) => {
    setActionBusy(true);
    setError(null);
    const next: 'Y' | 'N' = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'apr_sla_policy', 'apr_sla_policy_id', r.apr_sla_policy_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelectedSla(null);
    void reload();
  };

  const toggleAuthActive = async (r: AuthRow) => {
    setActionBusy(true);
    setError(null);
    const next: 'Y' | 'N' = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'apr_authority_limit', 'apr_authority_limit_id', r.apr_authority_limit_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelectedAuth(null);
    void reload();
  };

  const isSla = section === 'sla';
  const listEmpty = isSla ? filteredSla.length === 0 : filteredAuth.length === 0;

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.apr.title', 'SLA & Authority')}
        description={t('mobile.apr.description', 'Approval SLA policies and per-user or per-role authority limits.')}
      />

      <MobileStatStrip
        stats={[
          { value: sla.length, label: t('mobile.apr.section_sla', 'SLA policies') },
          { value: auth.length, label: t('mobile.apr.section_auth', 'Authority limits') },
          { value: activeSlaCount + activeAuthCount, label: t('mobile.apr.active_kpi', 'Active') },
        ]}
      />

      <MobileFilterChips
        chips={sections.map((sctn) => ({ value: sctn.key, label: sctn.label }))}
        isActive={(v) => section === v}
        onSelect={(v) => setSection(v as Section)}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.apr.search', 'Search')}
          aria-label={t('mobile.apr.search', 'Search')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <div className="m-card">
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </div>
      )}

      {listEmpty && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.apr.none', 'Nothing matches')}</div></MobileContentCard>
      ) : isSla ? (
        <MobileContentCard list>
          {filteredSla.map((r) => (
            <MobileCardRow
              key={r.apr_sla_policy_id}
              lead={<Timer />}
              leadTone={r.is_active === 'Y' ? 'blue' : 'slate'}
              title={configName(r.approval_config_id)}
              subtitle={
                (r.level === null ? t('mobile.apr.any_level', 'Any level') : t('mobile.apr.level_n', 'Level {{n}}', { n: r.level }))
                + ' · ' + minutesLabel(r.due_minutes) + ' · ' + enumLabel(BREACH_ACTIONS, r.breach_action)
              }
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelectedSla(r)}
            />
          ))}
        </MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filteredAuth.map((r) => (
            <MobileCardRow
              key={r.apr_authority_limit_id}
              lead={<Gauge />}
              leadTone={r.is_active === 'Y' ? 'blue' : 'slate'}
              title={`${enumLabel(SCOPES, r.scope)} · ${r.scope_ref || t('mobile.apr.any', 'Any')}`}
              subtitle={`${r.dimension} · ${t('mobile.apr.max_n', 'Max {{n}}', { n: r.max_value.toLocaleString() })}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelectedAuth(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* SLA detail bottom-sheet */}
      <div className={'m-scrim' + (selectedSla ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelectedSla(null); }} />
      <div className={'m-sheet' + (selectedSla ? ' m-open' : '')} aria-label={t('mobile.apr.sla_detail', 'SLA policy detail')}>
        <div className="m-grabber" />
        {selectedSla && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Timer size={20} /></span>
              <span>
                <div className="m-nm">{configName(selectedSla.approval_config_id)}</div>
                <div className="m-em">
                  {selectedSla.level === null ? t('mobile.apr.any_level', 'Any level') : t('mobile.apr.level_n', 'Level {{n}}', { n: selectedSla.level })}
                </div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.apr.status', 'Status')}
                value={<MobileStatusPill tone={selectedSla.is_active === 'Y' ? 'ok' : 'no'}>{selectedSla.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.apr.due', 'Due window')} value={minutesLabel(selectedSla.due_minutes)} />
              <MobileDetailRow label={t('mobile.apr.reminder', 'Reminder')} value={minutesLabel(selectedSla.reminder_minutes)} />
              <MobileDetailRow label={t('mobile.apr.breach', 'Breach action')} value={enumLabel(BREACH_ACTIONS, selectedSla.breach_action)} />
              <MobileDetailRow
                label={t('mobile.apr.calendar', 'Business calendar')}
                value={<MobileStatusPill tone={selectedSla.use_business_calendar === 'Y' ? 'ok' : 'no'}>{selectedSla.use_business_calendar === 'Y' ? t('common.boolean.on', 'On') : t('common.boolean.off', 'Off')}</MobileStatusPill>}
              />
              {selectedSla.use_business_calendar === 'Y' && (
                <MobileDetailRow label={t('mobile.apr.calendar_code', 'Calendar code')} value={selectedSla.business_calendar_code || t('common.none', 'None')} />
              )}
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => { setEditingSla(selectedSla); setSelectedSla(null); }}>
                <SquarePen size={16} />
                {t('mobile.apr.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selectedSla.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleSlaActive(selectedSla)}
              >
                <Power size={16} />
                {selectedSla.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelectedSla(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Authority detail bottom-sheet */}
      <div className={'m-scrim' + (selectedAuth ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelectedAuth(null); }} />
      <div className={'m-sheet' + (selectedAuth ? ' m-open' : '')} aria-label={t('mobile.apr.auth_detail', 'Authority limit detail')}>
        <div className="m-grabber" />
        {selectedAuth && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Gauge size={20} /></span>
              <span>
                <div className="m-nm">{enumLabel(SCOPES, selectedAuth.scope)} · {selectedAuth.scope_ref || t('mobile.apr.any', 'Any')}</div>
                <div className="m-em">{selectedAuth.dimension}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.apr.status', 'Status')}
                value={<MobileStatusPill tone={selectedAuth.is_active === 'Y' ? 'ok' : 'no'}>{selectedAuth.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.apr.scope', 'Scope')} value={enumLabel(SCOPES, selectedAuth.scope)} />
              <MobileDetailRow label={t('mobile.apr.reference', 'Reference')} value={selectedAuth.scope_ref || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.apr.entity', 'Entity')} value={selectedAuth.entity_name || t('mobile.apr.all_entities', 'All entities')} />
              <MobileDetailRow label={t('mobile.apr.dimension', 'Dimension')} value={selectedAuth.dimension} />
              <MobileDetailRow label={t('mobile.apr.max_value', 'Max value')} value={selectedAuth.max_value.toLocaleString()} />
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => { setEditingAuth(selectedAuth); setSelectedAuth(null); }}>
                <SquarePen size={16} />
                {t('mobile.apr.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selectedAuth.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleAuthActive(selectedAuth)}
              >
                <Power size={16} />
                {selectedAuth.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelectedAuth(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <SlaEditSheet
        session={session}
        row={editingSla}
        configs={configs}
        onClose={() => setEditingSla(null)}
        onSaved={() => { setEditingSla(null); void reload(); }}
      />
      <AuthEditSheet
        session={session}
        row={editingAuth}
        onClose={() => setEditingAuth(null)}
        onSaved={() => { setEditingAuth(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// SLA edit sheet — scalar policy fields saved through the Common Routine.
// ---------------------------------------------------------------------------

interface SlaEditSheetProps {
  session: AuthSession;
  row: SlaRow | null;
  configs: ConfigRow[];
  onClose: () => void;
  onSaved: () => void;
}

function SlaEditSheet({ session, row, configs, onClose, onSaved }: SlaEditSheetProps) {
  const { t } = useTranslation();
  const [configId, setConfigId] = useState<number | null>(null);
  const [level, setLevel] = useState('');
  const [dueMinutes, setDueMinutes] = useState('0');
  const [reminderMinutes, setReminderMinutes] = useState('');
  const [breachAction, setBreachAction] = useState('NOTIFY');
  const [useCalendar, setUseCalendar] = useState(false);
  const [calendarCode, setCalendarCode] = useState('');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    setConfigId(row.approval_config_id);
    setLevel(row.level === null ? '' : String(row.level));
    setDueMinutes(String(row.due_minutes));
    setReminderMinutes(row.reminder_minutes === null ? '' : String(row.reminder_minutes));
    setBreachAction(row.breach_action);
    setUseCalendar(row.use_business_calendar === 'Y');
    setCalendarCode(row.business_calendar_code ?? '');
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    if (configId === null) { setError(t('mobile.apr.config_required', 'A config is required.')); return; }
    const due = Number(dueMinutes);
    if (!Number.isFinite(due) || due < 0) { setError(t('mobile.apr.due_invalid', 'Due window must be zero or more minutes.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'apr_sla_policy', 'apr_sla_policy_id', row.apr_sla_policy_id, {
      approval_config_id: configId,
      level: level.trim() === '' ? null : Number(level),
      due_minutes: due,
      reminder_minutes: reminderMinutes.trim() === '' ? null : Number(reminderMinutes),
      breach_action: breachAction,
      use_business_calendar: useCalendar ? 'Y' : 'N',
      business_calendar_code: useCalendar && calendarCode.trim() !== '' ? calendarCode.trim() : null,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.apr.sla_edit', 'Edit SLA policy')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Timer size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.apr.sla_edit', 'Edit SLA policy')}</div>
                <div className="m-em">{t('mobile.apr.sla_edit_sub', 'Due window, reminders and breach action')}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-sla-config">{t('mobile.apr.config', 'Config')}</label>
                <select
                  id="m-sla-config"
                  className="m-sel"
                  value={configId ?? ''}
                  onChange={(e) => setConfigId(e.target.value === '' ? null : Number(e.target.value))}
                >
                  {configs.map((c) => (
                    <option key={c.approval_config_id} value={c.approval_config_id}>{c.internal_code}</option>
                  ))}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-sla-level">{t('mobile.apr.level', 'Level')}</label>
                <input
                  id="m-sla-level"
                  className="m-inp"
                  type="number"
                  value={level}
                  placeholder={t('mobile.apr.any_level', 'Any level')}
                  onChange={(e) => setLevel(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-sla-due">{t('mobile.apr.due', 'Due window')} ({t('mobile.apr.minutes_unit', 'minutes')})</label>
                <input
                  id="m-sla-due"
                  className="m-inp"
                  type="number"
                  value={dueMinutes}
                  onChange={(e) => setDueMinutes(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-sla-remind">{t('mobile.apr.reminder', 'Reminder')} ({t('mobile.apr.minutes_unit', 'minutes')})</label>
                <input
                  id="m-sla-remind"
                  className="m-inp"
                  type="number"
                  value={reminderMinutes}
                  placeholder={t('common.none', 'None')}
                  onChange={(e) => setReminderMinutes(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-sla-breach">{t('mobile.apr.breach', 'Breach action')}</label>
                <select id="m-sla-breach" className="m-sel" value={breachAction} onChange={(e) => setBreachAction(e.target.value)}>
                  {BREACH_ACTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.apr.calendar', 'Business calendar')}</div>
                  <div className="m-formrow-s">{t('mobile.apr.calendar_hint', 'Measure the due window in business hours')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={useCalendar}
                  className={'m-switch ' + (useCalendar ? 'm-on' : 'm-off')}
                  onClick={() => setUseCalendar((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              {useCalendar && (
                <div className="m-formrow">
                  <label htmlFor="m-sla-calcode">{t('mobile.apr.calendar_code', 'Calendar code')}</label>
                  <input
                    id="m-sla-calcode"
                    className="m-inp"
                    value={calendarCode}
                    onChange={(e) => setCalendarCode(e.target.value)}
                  />
                </div>
              )}
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.apr.active_hint', 'Policy is enforced by the engine')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Authority-limit edit sheet — saved through the Common Routine.
// ---------------------------------------------------------------------------

interface AuthEditSheetProps {
  session: AuthSession;
  row: AuthRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function AuthEditSheet({ session, row, onClose, onSaved }: AuthEditSheetProps) {
  const { t } = useTranslation();
  const [scope, setScope] = useState('ROLE');
  const [scopeRef, setScopeRef] = useState('');
  const [entityName, setEntityName] = useState('');
  const [dimension, setDimension] = useState('');
  const [maxValue, setMaxValue] = useState('0');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) return;
    setScope(row.scope);
    setScopeRef(row.scope_ref);
    setEntityName(row.entity_name ?? '');
    setDimension(row.dimension);
    setMaxValue(String(row.max_value));
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    if (scopeRef.trim() === '') { setError(t('mobile.apr.ref_required', 'A reference (user id or role code) is required.')); return; }
    if (dimension.trim() === '') { setError(t('mobile.apr.dimension_required', 'A dimension is required.')); return; }
    const max = Number(maxValue);
    if (!Number.isFinite(max)) { setError(t('mobile.apr.max_invalid', 'Max value must be a number.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'apr_authority_limit', 'apr_authority_limit_id', row.apr_authority_limit_id, {
      scope,
      scope_ref: scopeRef.trim(),
      entity_name: entityName.trim() === '' ? null : entityName.trim(),
      dimension: dimension.trim(),
      max_value: max,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.apr.auth_edit', 'Edit authority limit')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Gauge size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.apr.auth_edit', 'Edit authority limit')}</div>
                <div className="m-em">{t('mobile.apr.auth_edit_sub', 'Approval threshold per user or role')}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-auth-scope">{t('mobile.apr.scope', 'Scope')}</label>
                <select id="m-auth-scope" className="m-sel" value={scope} onChange={(e) => setScope(e.target.value)}>
                  {SCOPES.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.fallback)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-auth-ref">{t('mobile.apr.reference', 'Reference')}</label>
                <input
                  id="m-auth-ref"
                  className="m-inp"
                  value={scopeRef}
                  placeholder={t('mobile.apr.ref_hint', 'User id or role code')}
                  onChange={(e) => setScopeRef(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-auth-entity">{t('mobile.apr.entity', 'Entity')}</label>
                <input
                  id="m-auth-entity"
                  className="m-inp"
                  value={entityName}
                  placeholder={t('mobile.apr.all_entities', 'All entities')}
                  onChange={(e) => setEntityName(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-auth-dimension">{t('mobile.apr.dimension', 'Dimension')}</label>
                <input
                  id="m-auth-dimension"
                  className="m-inp"
                  value={dimension}
                  onChange={(e) => setDimension(e.target.value)}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-auth-max">{t('mobile.apr.max_value', 'Max value')}</label>
                <input
                  id="m-auth-max"
                  className="m-inp"
                  type="number"
                  value={maxValue}
                  onChange={(e) => setMaxValue(e.target.value)}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.apr.active_hint_auth', 'Limit is applied by the engine')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
